import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { request, requestVoid } from '@/shared/api'
import {
  type AccessRole,
  type DeactivateResult,
  DeactivateResultSchema,
  type Employee,
  EmployeeListSchema,
  EmployeeSchema,
  type NewEmployeeInput,
  type TelegramLink,
  TelegramLinkSchema,
} from '../model/schema'

export const employeesQueryKey = ['employees'] as const

/**
 * Команда — драйвер колонок сетки. Порядок стабилен (бэкенд сортирует по имени):
 * колонки не должны прыгать между открытиями.
 */
export function employeesQueryOptions(includeInactive = false) {
  return queryOptions({
    queryKey: [...employeesQueryKey, { includeInactive }] as const,
    queryFn: (): Promise<Employee[]> =>
      request('/employees', EmployeeListSchema, {
        query: { include_inactive: includeInactive },
      }),
    staleTime: 5 * 60_000,
  })
}

export function useCreateEmployeeMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ accessRole, ...rest }: NewEmployeeInput): Promise<Employee> =>
      request('/employees', EmployeeSchema, {
        method: 'POST',
        body: { ...rest, ...(accessRole ? { access_role: accessRole } : {}) },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: employeesQueryKey }),
  })
}

/**
 * Правка карточки. Имя и должность заменяются целиком — ручка не tri-state,
 * поэтому форма всегда шлёт текущие значения обоих. Права — поле отдельное: не
 * передали, значит не трогаем.
 */
export function useUpdateEmployeeMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      name,
      role,
      accessRole,
    }: {
      id: string
      name: string
      role: string
      accessRole?: AccessRole
    }): Promise<Employee> =>
      request(`/employees/${id}`, EmployeeSchema, {
        method: 'PATCH',
        body: { name, role, ...(accessRole ? { access_role: accessRole } : {}) },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: employeesQueryKey }),
  })
}

export function useSetEmployeeActiveMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }): Promise<DeactivateResult> =>
      request(`/employees/${id}/active`, DeactivateResultSchema, {
        method: 'PUT',
        body: { active },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: employeesQueryKey }),
  })
}

/** Выдать пароль. Свой меняет каждый, чужой выдаёт только администратор. */
export function useSetPasswordMutation() {
  return useMutation({
    mutationFn: ({
      employeeId,
      newPassword,
      currentPassword,
    }: {
      employeeId: string
      newPassword: string
      currentPassword?: string
    }): Promise<void> =>
      requestVoid(`/employees/${employeeId}/password`, {
        method: 'POST',
        body: {
          new_password: newPassword,
          ...(currentPassword === undefined ? {} : { current_password: currentPassword }),
        },
      }),
  })
}

/**
 * Привязка только своего мессенджера (`/employees/me/...`). Чужую завести
 * нельзя по устройству: адрес чата берётся из апдейта мессенджера, а ссылку
 * должен открыть сам человек — иначе привязку оформят на чужой аккаунт.
 */
export function useStartTelegramLinkMutation() {
  return useMutation({
    mutationFn: (): Promise<TelegramLink> =>
      request('/employees/me/telegram/link', TelegramLinkSchema, { method: 'POST' }),
  })
}

export function useUnlinkTelegramMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (): Promise<void> => requestVoid('/employees/me/telegram', { method: 'DELETE' }),
    onSettled: () => qc.invalidateQueries({ queryKey: employeesQueryKey }),
  })
}
